import React, { useEffect, useMemo, useState } from "react";
import { useLive } from "@/lib/live";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Plus, Trash, Receipt, CurrencyInr, Wallet, Calendar, PencilSimple, X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { istToday, istDate } from "@/lib/istDate";

const inr = (n) => `₹${(Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const todayStr = () => istToday();
const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s.length === 10 ? s + "T00:00:00" : s)
      .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch (_e) { return s; }
};

export default function DailyExpensesPage() {
  const { user } = useAuth();
  const canUse = user?.role === "admin" || user?.role === "secondary_admin" || user?.role === "accountant";

  // Date range — default: today only
  const [fromDate, setFromDate] = useState(todayStr());
  const [toDate, setToDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);

  useLive(() => { load(); });
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/expenses/daily?from=${fromDate}&to=${toDate}`);
      setRows(data.rows || []);
      setSummary(data.summary || []);
      setGrandTotal(data.grand_total || 0);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Expenses load fail hua");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: cats }, { data: accts }] = await Promise.all([
          api.get("/accounts/expense-categories"),
          api.get("/accounts/financial-accounts"),
        ]);
        // Only show operating_expense category keys here — this is a shop-level
        // daily tracker, not a full accounts P&L entry panel. Keeps the dropdown
        // short and focused on chai, fuel, dihadi, electricity, repair, misc, etc.
        setCategories((cats || []).filter((c) => c.transaction_class === "operating_expense"));
        setAccounts((accts || []).filter((a) => a.is_active !== false));
      } catch (e) {
        toast.error("Setup load fail hua");
      }
    })();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromDate, toDate]);

  const deleteExpense = async (id) => {
    if (!window.confirm("Is expense ko delete kar dein? Account balance wapas aa jayega.")) return;
    try {
      await api.delete(`/expenses/daily/${id}`);
      toast.success("Expense delete ho gaya");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete fail hua");
    }
  };

  const quickRanges = useMemo(() => {
    const d = new Date();
    const iso = (dt) => istDate(dt);
    const today = iso(d);
    const yest = iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1));
    const weekStart = iso(new Date(d.getFullYear(), d.getMonth(), d.getDate() - 6));
    const monthStart = iso(new Date(d.getFullYear(), d.getMonth(), 1));
    return [
      { label: "Today", from: today, to: today },
      { label: "Yesterday", from: yest, to: yest },
      { label: "This Week", from: weekStart, to: today },
      { label: "This Month", from: monthStart, to: today },
    ];
  }, []);

  const accountById = (id) => accounts.find((a) => a.id === id);
  const categoryLabel = (key) => categories.find((c) => c.key === key)?.label || key;

  if (!canUse) {
    return (
      <div className="p-10 text-center text-slate-400">Daily Expenses access sirf admin/secondary admin/accountant ke liye hai.</div>
    );
  }

  return (
    <div className="space-y-5" data-testid="daily-expenses-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs font-mono-num tracking-widest text-slate-400">// DAILY EXPENSES</div>
          <h1 className="font-heading text-3xl font-black text-white">Daily Expenses</h1>
          <p className="text-slate-400 text-sm mt-1">
            Chai-pani, fuel, dihadi, electricity, repair, misc — har roz ka kharcha ek jagah track karo.
          </p>
        </div>
        <Button
          onClick={() => setAddOpen(true)}
          className="rounded-sm h-11 bg-orange-500 hover:bg-orange-600 text-white font-mono-num tracking-wider"
          data-testid="add-expense-button"
        >
          <Plus size={18} weight="bold" className="mr-1" /> ADD EXPENSE
        </Button>
      </div>

      {/* Date range + quick ranges */}
      <div className="rounded-sm border border-slate-800 bg-slate-900/40 p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[140px]">
          <Label className="text-xs text-slate-400 tracking-widest">FROM</Label>
          <Input
            type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="rounded-sm bg-slate-950 border-slate-700 h-10 font-mono-num"
            data-testid="daily-expenses-from"
          />
        </div>
        <div className="flex-1 min-w-[140px]">
          <Label className="text-xs text-slate-400 tracking-widest">TO</Label>
          <Input
            type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="rounded-sm bg-slate-950 border-slate-700 h-10 font-mono-num"
            data-testid="daily-expenses-to"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {quickRanges.map((r) => (
            <Button key={r.label} variant="outline" size="sm"
              onClick={() => { setFromDate(r.from); setToDate(r.to); }}
              className="rounded-sm h-9 border-slate-700 text-slate-300 hover:bg-slate-800 text-xs tracking-wider"
              data-testid={`daily-expenses-range-${r.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-sm border border-orange-500/40 bg-orange-500/5 p-5">
          <div className="text-xs tracking-widest text-orange-300 font-mono-num">TOTAL SPEND</div>
          <div className="font-mono-num text-3xl font-black text-orange-200 mt-1" data-testid="daily-expenses-grand-total">
            {inr(grandTotal)}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {fmtDate(fromDate)} — {fmtDate(toDate)} · {rows.filter(r => !r.reversed).length} entries
          </div>
        </div>
        <div className="rounded-sm border border-slate-800 bg-slate-900/50 p-5 md:col-span-2">
          <div className="text-xs tracking-widest text-slate-400 font-mono-num mb-3">BY CATEGORY</div>
          {summary.length === 0 ? (
            <div className="text-slate-500 text-sm py-2">Is period mein koi expense nahi.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {summary.map((s) => (
                <div key={s.category_key}
                  className="rounded-sm border border-slate-700 bg-slate-950/60 px-3 py-2"
                  data-testid={`cat-chip-${s.category_key}`}
                >
                  <div className="text-[10px] tracking-widest text-slate-400 font-mono-num uppercase">{s.label}</div>
                  <div className="font-mono-num text-sm font-bold text-white mt-0.5">{inr(s.total)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rows */}
      <div className="rounded-sm border border-slate-800 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] font-mono-num tracking-widest text-slate-400 bg-slate-900/60 border-b border-slate-800">
          <div className="col-span-2">DATE</div>
          <div className="col-span-3">CATEGORY</div>
          <div className="col-span-2">PAID FROM</div>
          <div className="col-span-3">NOTE</div>
          <div className="col-span-1 text-right">AMOUNT</div>
          <div className="col-span-1 text-right"></div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-400 font-mono-num text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt size={40} className="mx-auto text-slate-600 mb-2" />
            <div className="text-slate-400 text-sm">
              Is period mein koi expense record nahi hai. Upar <strong className="text-orange-300">ADD EXPENSE</strong> dabake pehla kharcha dalein.
            </div>
          </div>
        ) : (
          rows.map((r) => {
            const acc = accountById(r.financial_account_id);
            return (
              <div key={r.id}
                className={"grid grid-cols-12 gap-2 px-4 py-3 border-b border-slate-800 last:border-b-0 items-center text-sm "
                  + (r.reversed ? "opacity-50 line-through" : "hover:bg-slate-900/40")}
                data-testid={`expense-row-${r.id}`}
              >
                <div className="col-span-2 font-mono-num text-slate-200">{fmtDate(r.date)}</div>
                <div className="col-span-3 text-slate-100">
                  {categoryLabel(r.category_key)}
                  {r.paid_to && r.paid_to !== categoryLabel(r.category_key) && (
                    <span className="text-slate-500 text-xs ml-1">· {r.paid_to}</span>
                  )}
                </div>
                <div className="col-span-2 text-slate-300 text-xs">
                  {acc?.account_name || "—"}
                  <div className="text-slate-500 text-[10px]">{r.payment_method}</div>
                </div>
                <div className="col-span-3 text-slate-400 text-xs truncate" title={r.note}>{r.note || "—"}</div>
                <div className="col-span-1 text-right font-mono-num text-white font-bold">{inr(r.amount)}</div>
                <div className="col-span-1 text-right">
                  {!r.reversed && (
                    <Button size="sm" variant="ghost"
                      onClick={() => deleteExpense(r.id)}
                      className="h-7 w-7 p-0 rounded-sm text-slate-500 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-expense-${r.id}`}
                      title="Delete expense"
                    >
                      <Trash size={14} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <AddExpenseDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        categories={categories}
        accounts={accounts}
        onSaved={() => { setAddOpen(false); load(); }}
      />
    </div>
  );
}

function AddExpenseDialog({ open, onClose, categories, accounts, onSaved }) {
  const [form, setForm] = useState({
    date: todayStr(),
    category_key: "",
    amount: "",
    paid_to: "",
    financial_account_id: "",
    payment_method: "Cash",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({
        ...f,
        date: todayStr(),
        category_key: categories[0]?.key || "",
        financial_account_id: accounts.find((a) => a.account_type === "cash")?.id || accounts[0]?.id || "",
        amount: "",
        paid_to: "",
        note: "",
        payment_method: "Cash",
      }));
    }
  }, [open, categories, accounts]);

  const save = async () => {
    if (!form.category_key) return toast.error("Category chuno");
    if (!form.financial_account_id) return toast.error("Account chuno");
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Amount daalo (0 se zyada)");
    setSaving(true);
    try {
      await api.post("/expenses/daily", {
        date: form.date,
        category_key: form.category_key,
        amount: amt,
        paid_to: form.paid_to.trim(),
        financial_account_id: form.financial_account_id,
        payment_method: form.payment_method,
        note: form.note.trim(),
      });
      toast.success("Expense record ho gaya");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save fail hua");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm border-slate-800 bg-slate-950 max-w-lg" data-testid="add-expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
            <Receipt size={20} weight="fill" className="text-orange-400" /> Daily Expense Add Karo
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400 tracking-widest">DATE</Label>
              <Input type="date" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="rounded-sm bg-slate-900 border-slate-700 h-10 font-mono-num"
                data-testid="expense-date-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400 tracking-widest">AMOUNT (₹)</Label>
              <Input type="number" inputMode="decimal" step="0.01" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="e.g. 150"
                className="rounded-sm bg-slate-900 border-slate-700 h-10 font-mono-num text-lg font-bold"
                data-testid="expense-amount-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-400 tracking-widest">CATEGORY</Label>
            <Select value={form.category_key} onValueChange={(v) => setForm({ ...form, category_key: v })}>
              <SelectTrigger className="rounded-sm bg-slate-900 border-slate-700 h-10" data-testid="expense-category-select">
                <SelectValue placeholder="Chuno category..." />
              </SelectTrigger>
              <SelectContent className="rounded-sm bg-slate-900 border-slate-700 max-h-72">
                {categories.map((c) => (
                  <SelectItem key={c.key} value={c.key} data-testid={`expense-cat-opt-${c.key}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-slate-400 tracking-widest">PAID TO (optional)</Label>
            <Input value={form.paid_to}
              onChange={(e) => setForm({ ...form, paid_to: e.target.value })}
              placeholder="Vendor / person name"
              className="rounded-sm bg-slate-900 border-slate-700 h-10"
              data-testid="expense-paid-to-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400 tracking-widest">PAID FROM</Label>
              <Select value={form.financial_account_id}
                onValueChange={(v) => setForm({ ...form, financial_account_id: v })}>
                <SelectTrigger className="rounded-sm bg-slate-900 border-slate-700 h-10" data-testid="expense-account-select">
                  <SelectValue placeholder="Account" />
                </SelectTrigger>
                <SelectContent className="rounded-sm bg-slate-900 border-slate-700">
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.account_name} <span className="text-slate-500 text-xs">· {a.account_type}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400 tracking-widest">PAYMENT METHOD</Label>
              <Select value={form.payment_method}
                onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="rounded-sm bg-slate-900 border-slate-700 h-10" data-testid="expense-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm bg-slate-900 border-slate-700">
                  {["Cash", "UPI", "Bank Transfer", "Cheque", "Card", "Other"].map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-400 tracking-widest">NOTE (optional)</Label>
            <Textarea rows={2} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Konsa kaam, kis vendor ka, kya detail..."
              className="rounded-sm bg-slate-900 border-slate-700 resize-none"
              data-testid="expense-note-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}
              className="rounded-sm border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="expense-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}
              className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white font-mono-num tracking-wider disabled:opacity-60"
              data-testid="expense-save-button"
            >
              {saving ? "Saving…" : "SAVE EXPENSE"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
